import { useEffect } from 'react'
import type { Collection, CollectionVariant, Locale, MythDataset } from '../data/schema'
import { localize, localizeWithEnglish } from '../i18n'
import type { InspectorSnap } from './Inspector'
import { Icon } from './Icon'
import { TypeIcon } from './TypeIcon'

interface CollectionInspectorProps {
  locale: Locale
  dataset: MythDataset
  collection: Collection
  variantId: string
  snap: InspectorSnap
  onSnapChange: (snap: InspectorSnap) => void
  onDragHeightChange: (height: number | null) => void
  onClose: () => void
  onSelectVariant: (id: string) => void
  onSelectNode: (id: string) => void
  onOpenStory: (id: string) => void
  onViewOnAtlas: () => void
}

function selectedVariantFor(collection: Collection, variantId: string) {
  return collection.variants.find((variant) => variant.id === variantId)
    ?? collection.variants.find((variant) => variant.id === collection.defaultVariantId)
    ?? collection.variants[0]
}

function variantMemberIds(variant: CollectionVariant) {
  return new Set(variant.members.map((member) => member.nodeId))
}

export function CollectionInspector({
  locale,
  dataset,
  collection,
  variantId,
  snap,
  onSnapChange,
  onDragHeightChange,
  onClose,
  onSelectVariant,
  onSelectNode,
  onOpenStory,
  onViewOnAtlas,
}: CollectionInspectorProps) {
  const variant = selectedVariantFor(collection, variantId)
  const sourcesFor = (ids: string[]) => ids
    .map((id) => dataset.sources.find((source) => source.id === id))
    .filter(Boolean)
  const memberRows = variant.members.map((member, index) => ({
    member,
    index,
    node: dataset.nodes.find((node) => node.id === member.nodeId),
    sources: sourcesFor(member.sourceRefs.length ? member.sourceRefs : variant.sourceRefs),
  }))
  const variantIds = collection.variants.map((candidate) => candidate.id)
  const activeIndex = Math.max(0, variantIds.indexOf(variant.id))
  const collectionSources = sourcesFor([...new Set([...collection.sourceRefs, ...variant.sourceRefs])])
  const relatedStories = collection.relatedStoryIds
    .map((id) => dataset.stories.find((story) => story.id === id))
    .filter(Boolean)
  const relatedCycles = collection.relatedCycleIds
    .map((id) => dataset.storyCycles.find((cycle) => cycle.id === id))
    .filter(Boolean)
  const comparison = collection.variants.map((candidate) => {
    const candidateIds = variantMemberIds(candidate)
    const otherMembers = collection.variants
      .filter((other) => other.id !== candidate.id)
      .flatMap((other) => other.members.map((member) => member.nodeId))
    const uniqueIds = [...candidateIds].filter((id) => !otherMembers.includes(id))
    return {
      variant: candidate,
      members: candidate.members
        .map((member) => dataset.nodes.find((node) => node.id === member.nodeId))
        .filter(Boolean),
      uniqueMembers: uniqueIds
        .map((id) => dataset.nodes.find((node) => node.id === id))
        .filter(Boolean),
    }
  })

  useEffect(() => () => onDragHeightChange(null), [onDragHeightChange])

  const closeLabel = locale === 'en' ? 'Clear list' : '清除名單'
  const variantLabel = localize(variant.label, locale)
  const memberCountLabel = locale === 'en'
    ? `${variant.members.length} names`
    : `${variant.members.length} 個名字`

  return (
    <aside
      className={`inspector collection-inspector inspector-${snap}`}
      data-testid="inspector"
      data-collection-id={collection.id}
      data-collection-variant-id={variant.id}
    >
      <button
        type="button"
        className="sheet-handle"
        aria-label={locale === 'en' ? snap === 'peek' ? 'Expand details' : 'Collapse details' : snap === 'peek' ? '展開詳情' : '收起詳情'}
        aria-expanded={snap !== 'peek'}
        onClick={() => onSnapChange(snap === 'peek' ? 'half' : 'peek')}
      ><span /></button>

      <div className="mobile-peek-summary">
        <span className="type-sigil peek-sigil collection"><Icon name="list" size={15} /></span>
        <span className="peek-copy"><strong>{localize(collection.title, locale)}</strong><small>{variantLabel} · {memberCountLabel}</small></span>
        <button className="peek-expand" type="button" onClick={() => onSnapChange('half')}>{locale === 'en' ? 'View details' : '查看詳情'}</button>
        <button className="icon-button mobile-peek-close" type="button" onClick={onClose} aria-label={closeLabel}><Icon name="close" /></button>
      </div>

      <header className="inspector-head">
        <span className="eyebrow">{locale === 'en' ? 'Reference list' : '參考名單'}</span>
        <span className="inspector-head-actions">
          <button className="sheet-size-button" type="button" onClick={() => onSnapChange(snap === 'full' ? 'half' : 'full')}>{locale === 'en' ? snap === 'full' ? 'Back to graph' : 'Full reading' : snap === 'full' ? '返回地圖' : '完整閱讀'}</button>
          <button className="icon-button" onClick={onClose} aria-label={closeLabel}><Icon name="close" /></button>
        </span>
      </header>

      <section className="profile-section collection-overview" aria-label={locale === 'en' ? 'Reference list overview' : '參考名單概覽'}>
        <h2>{localizeWithEnglish(collection.title, locale)}</h2>
        <p className="inspector-summary">{localize(collection.description, locale)}</p>
        <div className="collection-policy">
          <strong>{locale === 'en' ? 'Who is included' : '名單收錄方式'}</strong>
          <p>{localize(collection.inclusionPolicy, locale)}</p>
        </div>
        <div className="collection-atlas-actions">
          <button type="button" className="primary-button" onClick={onViewOnAtlas}>
            <Icon name="focus" size={16} />
            <span>{locale === 'en' ? 'View on Atlas' : '在地圖查看'}</span>
          </button>
          <span role="status">{locale === 'en'
            ? `${variantLabel} shows ${variant.members.length} names on the Atlas.`
            : `${variantLabel} 會在地圖標示 ${variant.members.length} 個名字。`}</span>
        </div>
      </section>

      <section className="profile-section collection-variants" aria-label={locale === 'en' ? 'List versions' : '名單版本'}>
        <h3>{locale === 'en' ? 'Version' : '版本'}</h3>
        <div className="variant-switcher" role="tablist" aria-label={locale === 'en' ? 'List versions' : '名單版本'}>
          {collection.variants.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              role="tab"
              aria-selected={candidate.id === variant.id}
              className={candidate.id === variant.id ? 'active' : ''}
              onClick={() => onSelectVariant(candidate.id)}
            >
              <span>{localize(candidate.label, locale)}</span>
              <small>{candidate.members.length}</small>
            </button>
          ))}
        </div>
        <article className="collection-attribution">
          <strong>{variantLabel}</strong>
          <p>{localize(variant.description, locale)}</p>
          <small>{localize(variant.attribution, locale)}</small>
        </article>
        <div className="variant-stepper">
          <button type="button" onClick={() => onSelectVariant(variantIds[Math.max(0, activeIndex - 1)])} disabled={activeIndex === 0}><Icon name="arrow-left" />{locale === 'en' ? 'Previous' : '上一個'}</button>
          <button type="button" onClick={() => onSelectVariant(variantIds[Math.min(variantIds.length - 1, activeIndex + 1)])} disabled={activeIndex === variantIds.length - 1}>{locale === 'en' ? 'Next' : '下一個'}<Icon name="arrow-right" /></button>
        </div>
      </section>

      <section className="profile-section collection-members" aria-label={locale === 'en' ? 'Names in this list' : '名單成員'}>
        <h3>{locale === 'en' ? 'Names in this list' : '名單成員'}</h3>
        <ol>
          {memberRows.map(({ member, node, sources, index }) => node && (
            <li key={`${variant.id}-${member.nodeId}`}>
              <button type="button" onClick={() => onSelectNode(member.nodeId)}>
                <span className="collection-member-order">{String(index + 1).padStart(2, '0')}</span>
                <span className={`type-sigil ${node.type}`}><TypeIcon type={node.type} /></span>
                <span>
                  <strong>{localizeWithEnglish(node.name, locale)}</strong>
                  <small>{member.note ? localize(member.note, locale) : localize(node.summary, locale)}</small>
                </span>
                <Icon name="chevron" size={16} />
              </button>
              <div className="collection-member-sources">
                {sources.slice(0, 2).map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer">
                  <Icon name="book" size={12} />
                  <span>{localize(source.locator, locale)}</span>
                </a>)}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="profile-section collection-comparison" aria-label={locale === 'en' ? 'Compare versions' : '比較版本'}>
        <h3>{locale === 'en' ? 'Compare versions' : '比較版本'}</h3>
        {comparison.map(({ variant: candidate, members, uniqueMembers }) => (
          <article key={candidate.id} className={candidate.id === variant.id ? 'active' : ''}>
            <strong>{localize(candidate.label, locale)}</strong>
            <p>{members.map((node) => node ? localize(node.name, locale) : '').join(' · ')}</p>
            {uniqueMembers.length > 0 && <small>{locale === 'en' ? 'Only in this version: ' : '只在此版本：'}{uniqueMembers.map((node) => node ? localize(node.name, locale) : '').join(' · ')}</small>}
          </article>
        ))}
      </section>

      <section className="profile-section collection-exclusions" aria-label={locale === 'en' ? 'Not included' : '未列入'}>
        <h3>{locale === 'en' ? 'Not included' : '未列入'}</h3>
        <ul>{collection.exclusions.map((exclusion, index) => <li key={index}>{localize(exclusion, locale)}</li>)}</ul>
      </section>

      {(relatedStories.length > 0 || relatedCycles.length > 0) && <section className="profile-section collection-related" aria-label={locale === 'en' ? 'Related stories and cycles' : '相關故事與系列'}>
        <h3>{locale === 'en' ? 'Related stories' : '相關故事'}</h3>
        {relatedStories.map((story) => story && <button key={story.id} type="button" onClick={() => onOpenStory(story.id)} disabled={story.status !== 'available'}>
          <Icon name="myths" size={15} />
          <span><strong>{localizeWithEnglish(story.title, locale)}</strong><small>{localize(story.description, locale)}</small></span>
          <Icon name="arrow-right" size={15} />
        </button>)}
        {relatedCycles.map((cycle) => cycle && <p key={cycle.id}>{localizeWithEnglish(cycle.name, locale)}</p>)}
      </section>}

      {collectionSources.length > 0 && <section className="profile-section source-list" aria-label={locale === 'en' ? 'Sources' : '出處'}>
        <h3>{locale === 'en' ? 'Sources' : '出處'}</h3>
        {collectionSources.map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer">
          <Icon name="book" size={13} />
          <span><strong>{localize(source.work, locale)}</strong><small>{localize(source.locator, locale)}</small></span>
        </a>)}
      </section>}
    </aside>
  )
}
